<template>
  <View align="center" gap="lg" wrap="wrap">
    <img v-if="imageUrl" :src="imageUrl" :alt="alt" class="tile" />
    <div v-else class="tile placeholder"><slot name="placeholder" /></div>

    <View align="center" gap="sm">
      <Button variant="outline" size="medium" :loading="isUploading" @click="inputRef?.click()">
        <Icon name="upload-cloud-02" size="small" />
        {{ uploadLabel }}
      </Button>
      <Button v-if="imageUrl" variant="ghost" size="medium" :loading="isRemoving" @click="emit('remove')">
        Remove
      </Button>
    </View>
    <Text v-if="hint" variant="body-small" color="tertiary">{{ hint }}</Text>
    <input ref="inputRef" class="input" type="file" accept="image/*" @change="onPicked" />
  </View>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { Button, Icon, Text, useAlertsStore, View } from '@tallo/design-system';

import { checkImageFile, maxImageUploadMb } from '../utils/check-image-file.util';

/**
 * One picture with a preview: the company logo, a building's cover. The type and size are checked
 * here; what happens with the file (upload now, upload on save) is the parent's.
 */
const props = withDefaults(
  defineProps<{
    imageUrl?: string | null;
    alt?: string;
    uploadLabel?: string;
    hint?: string;
    isUploading?: boolean;
    isRemoving?: boolean;
    maxSizeMb?: number;
  }>(),
  {
    imageUrl: null,
    alt: '',
    uploadLabel: 'Upload photo',
    hint: '',
    isUploading: false,
    isRemoving: false,
    maxSizeMb: maxImageUploadMb,
  },
);
const emit = defineEmits<{ pick: [file: File]; remove: [] }>();

const alertsStore = useAlertsStore();
const inputRef = ref<HTMLInputElement>();

function onPicked(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  // Forgetting the selection lets the same file be picked again after a failed upload
  input.value = '';

  if (!file) {
    return;
  }

  const refusal = checkImageFile(file, props.maxSizeMb);

  if (refusal) {
    alertsStore.error(refusal);
    return;
  }

  emit('pick', file);
}
</script>

<style scoped lang="scss">
.tile {
  width: var(--t-spacing-4xl);
  height: var(--t-spacing-4xl);
  border-radius: var(--t-border-radius-md);
  object-fit: cover;
  flex-shrink: 0;
}

.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--t-color-blue-dust-100);
  color: var(--t-color-blue-dust-600);
  font: var(--t-font-weight-semi-bold) var(--t-font-size-600) var(--t-font-family-primary);
}

.input {
  display: none;
}
</style>
